import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import SiteIcon from '@components/icon/SiteIcons'
import Icon from '@components/icon/Icons'

import useBreakpoint from '@utils/useBreakpoints'

type HeaderColor = 'default' | 'primary'

interface Props {
  buttons?: HeaderButton[]
  centered?: boolean
  description?: string
  links?: HeaderLink[]
  renderBottom?: () => React.ReactNode
  siteIcon?: SiteIconName
  title: string
  color?: HeaderColor
  resizeOn?: string
  size?: 'small' | 'default'
  onResize?: () => void
}

const Header = ({
  buttons = [],
  centered = false,
  description,
  links = [],
  renderBottom,
  siteIcon,
  title,
  color,
  resizeOn,
  size = 'default',
  onResize,
}: Props) => {
  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const renderLink = (link: HeaderLink, index: number, tab: HeaderLink[]) => {
    const isFirst = index === 0
    const isLast = index === tab.length - 1
    return (
      <TouchableOpacity key={`link_${link.label}`} activeOpacity={0.9} onPress={link.onClick}>
        <LinkLabel color={color} isLast={isLast}>{`${!isFirst ? '/ ' : ''}${link.label}${
          !isLast ? ' ' : ''
        }`}</LinkLabel>
      </TouchableOpacity>
    )
  }

  const renderButton = (button: HeaderButton) => {
    return (
      <ButtonContainer key={`button_${button.label}`}>
        <Button
          status={button.status || 'active'}
          leftIcon={button.leftIcon}
          label={button.label}
          onClick={button.onClick}
          color={color === 'primary' ? 'light' : button.color}
        />
      </ButtonContainer>
    )
  }

  return (
    <Container color={color} centered={centered} overflow={!!renderBottom ? 'visible' : 'hidden'}>
      {!!siteIcon && (
        <SiteIconContainer>
          <SiteIcon name={siteIcon} color={theme.colors.primaryDark} height={130} />
        </SiteIconContainer>
      )}

      <TopRow>
        <Titles>
          {!isPhone && links.length > 0 && <Links>{links.map(renderLink)}</Links>}
          <Title color={color} centered={centered} numberOfLines={2} ellipsizeMode="tail">
            {title}
          </Title>
          {!isPhone && !!description && <Description color={color}>{description}</Description>}
        </Titles>
        {!isPhone && buttons.length > 0 && (
          <Buttons align={!!links && links.length > 0}>{buttons.map(renderButton)}</Buttons>
        )}
      </TopRow>

      {size === 'default' && !!renderBottom && renderBottom()}

      {!!resizeOn && !!onResize && (
        <ResizeButton activeOpacity={0.9} onPress={onResize}>
          <ResizeText>{resizeOn}</ResizeText>
          <Icon name={size === 'small' ? 'arrow-down' : 'arrow-up'} size={30} />
        </ResizeButton>
      )}
    </Container>
  )
}

export default Header

const Container = styled(View)<{ centered: boolean; overflow: 'hidden' | 'visible'; color?: HeaderColor }>`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '20px 0px 7px 0px' : '32px 24px 16px 24px')};
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' || props.color === 'primary'
      ? props.theme.colors.primary
      : props.theme.colors.cardSecondary};
  border-bottom-width: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 1)}px;
  border-color: ${(props) => props.theme.colors.primaryFocused};
  border-style: solid;
  overflow: ${(props) => props.overflow};
  ${(props) => props.centered && props.theme.breakpoint === 'phone' && `min-height: 200px;`};
  z-index: 2;
`
const TopRow = styled(View)`
  flex-direction: ${(props) =>
    props.theme.breakpoint !== 'big' && props.theme.breakpoint !== 'mediumBig' && props.theme.breakpoint !== 'medium'
      ? 'column'
      : 'row'};
  z-index: 1;
`
const Titles = styled(View)`
  ${(props) =>
    (props.theme.breakpoint === 'big' ||
      props.theme.breakpoint === 'mediumBig' ||
      props.theme.breakpoint === 'medium') &&
    `flex: 1;`}
  min-width: 200px;
`
const Buttons = styled(View)<{ align: boolean }>`
  flex-flow: row wrap;
  flex: 2;
  margin-right: 8px;
  justify-content: flex-end;
  align-items: center;
  margin-top: ${(props) => (props.align ? 16 : 0)}px;
`
const ButtonContainer = styled(View)`
  margin: 8px;
  width: 240px;
`
const Title = styled(Text)<{ centered: boolean; color?: HeaderColor }>`
  ${(props) => props.theme.fonts.Title3};
  ${(props) =>
    (props.theme.breakpoint === 'phone' || props.color === 'primary') && `color: ${props.theme.colors.background};`}
  font-size: ${(props) => (props.theme.breakpoint === 'phone' ? 34 : 24)}px;
  ${(props) => props.theme.breakpoint === 'phone' && `line-height: 41px;`}
  margin-right: 16px;
`
const Links = styled(View)`
  margin-bottom: 7px;
  flex-direction: row;
`
const LinkLabel = styled(Text)<{ isLast: boolean; color?: HeaderColor }>`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  ${(props) =>
    props.color === 'primary'
      ? `color: ${props.theme.colors.background};`
      : !props.isLast && `color: ${props.theme.colors.disable};`}
`
const Description = styled(Text)<{ color?: HeaderColor }>`
  margin-top: 8px;
  ${(props) => props.theme.fonts.Body};
  font-size: 16px;
  color: ${(props) => (props.color === 'primary' ? props.theme.colors.background : props.theme.colors.disable)};
`
const SiteIconContainer = styled(View)`
  position: absolute;
  left: 0;
  top: -15px;
  opacity: 0.1;
`

const ResizeButton = styled(TouchableOpacity)`
  align-self: flex-end;
  padding: 5px;
  flex-direction: row;
  align-items: center;
  z-index: -10;
`

const ResizeText = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  margin-right: 5px;
`
